import React, { useContext } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import WomanIcon from "@mui/icons-material/Woman";
import ManIcon from "@mui/icons-material/Man";
import PhoneIcon from "@mui/icons-material/Phone";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorFallback } from "../../../../components/index";
import { isEmptyObject, removeEmpty, stringAvatar, stringToColor } from "utils";
import { Link } from "react-router-dom";
import { StoreContext } from "App";

function MainUserInfo({ user = {} }) {
  const { auth } = useContext(StoreContext);
  const authUser = auth.user;
  user = removeEmpty(user);
  const username = `${user.fullNameUA.firstName} ${user.fullNameUA.lastName} ${
    user.fullNameUA.middleName || ""
  }`.trim();
  const engUsername = !isEmptyObject(user.fullNameENG)
    ? `${user.fullNameENG.firstName} ${user.fullNameENG.lastName} ${
        user.fullNameENG.middleName || ""
      }`.trim()
    : "";

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Grid container sx={{ padding: "10px 0" }}>
        {authUser?._id === user._id && (
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={12}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              component={Link}
              to={`/cabinet/users/${user._id}/edit`}
              variant={"contained"}
            >
              Редагувати
            </Button>
          </Grid>
        )}
        <Grid
          item
          md={3}
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <Avatar
            alt={username}
            {...stringAvatar(username)}
            sx={{
              width: 200,
              height: 200,
              borderRadius: "8px",
              bgcolor: stringToColor(username),
            }}
          />
        </Grid>
        <Grid item md={9} sx={{ textAlign: "left" }}>
          <Typography component={"h2"} variant={"h5"}>{`${username} ${
            engUsername ? "(" + engUsername + ")" : ""
          }`}</Typography>
          <Box sx={{ display: "flex", alignItems: "center" }} marginTop={2}>
            <Typography
              component={"p"}
              variant={"p"}
            >{`Тип профілю: `}</Typography>
            <Stack direction="row" spacing={1} marginLeft={1}>
              <Chip label={user.accountType} variant="outlined" />
            </Stack>
          </Box>
          {user?.orgUnit && (
            <Box sx={{ display: "flex", alignItems: "center" }} marginTop={2}>
              <Typography
                component={"p"}
                variant={"p"}
              >{`Підрозділ: ${user?.orgUnit?.nameUk}`}</Typography>
            </Box>
          )}

          <Box sx={{ display: "flex", alignItems: "center" }} marginTop={2}>
            <Typography component={"p"} variant={"p"}>{`Стать:`}</Typography>
            {user.gender === "чол" ? (
              <ManIcon sx={{ fontSize: "1.5em" }} />
            ) : (
              <WomanIcon sx={{ fontSize: "1.5em" }} />
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }} marginTop={2}>
            <Typography
              component={"p"}
              variant={"p"}
            >{`UserId: ${user._id}`}</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }} marginTop={2}>
            <Typography component={"p"} variant={"p"}>
              {"Email:"}
            </Typography>
            <Typography component={"p"} variant={"p"} marginLeft={1}>
              {
                <a
                  href={`mailto:${user.email}`}
                  target={"_blank"}
                  rel="noreferrer"
                >
                  {user.email}
                </a>
              }
            </Typography>
          </Box>
          <Grid
            item
            sx={{ borderBottom: 1, borderColor: "divider", padding: "10px 0" }}
          >
            <List
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))",
                gridAutoFlow: "dense",
              }}
            >
              {user.birthday && (
                <ListItem>
                  <ListItemText
                    primary={`Дата народження: ${new Date(
                      user.birthday
                    ).toLocaleDateString()}`}
                  />
                </ListItem>
              )}
              {user.contacts?.tel1 && (
                <ListItem>
                  <ListItemIcon>
                    <PhoneIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={`Телефон`}
                    secondary={
                      <a href={`tel:${user.contacts?.tel1}`}>
                        {user.contacts?.tel1}
                      </a>
                    }
                  />
                </ListItem>
              )}
              {user.contacts?.tel2 && (
                <ListItem>
                  <ListItemIcon>
                    <PhoneIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={`Телефон`}
                    secondary={
                      <a href={`tel:${user.contacts?.tel2}`}>
                        {user.contacts?.tel2}
                      </a>
                    }
                  />
                </ListItem>
              )}
            </List>
          </Grid>
          {!isEmptyObject(user.passport) && (
            <Grid
              item
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                padding: "10px 0",
              }}
            >
              <Typography component={"b"} variant={"b"}>
                {"Паспорт:"}
              </Typography>
              <List
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))",
                  gridAutoFlow: "dense",
                }}
              >
                {user.passport?.seriesNumber && (
                  <ListItem>
                    <ListItemText
                      primary={`Серія номер:`}
                      secondary={user.passport?.seriesNumber}
                    />
                  </ListItem>
                )}
                {user.passport?.issuedBy && (
                  <ListItem>
                    <ListItemText
                      primary={`Ким видано:`}
                      secondary={user.passport?.issuedBy}
                    />
                  </ListItem>
                )}
                {user.passport?.idPassport && (
                  <ListItem>
                    <ListItemText
                      primary={`ID паспорт (або картка):`}
                      secondary={user.passport?.idPassport}
                    />
                  </ListItem>
                )}
                {user.passport?.unzr && (
                  <ListItem>
                    <ListItemText
                      primary={`Код УНЗР:`}
                      secondary={user.passport?.unzr}
                    />
                  </ListItem>
                )}
                {user.passport?.issuedById && (
                  <ListItem>
                    <ListItemText
                      primary={`Ким видано (ID паспорт):`}
                      secondary={user.passport?.issuedById}
                    />
                  </ListItem>
                )}
                {user.passport?.dateOfIssueId && (
                  <ListItem>
                    <ListItemText
                      primary={`Дата видачі:`}
                      secondary={user.passport?.dateOfIssueId}
                    />
                  </ListItem>
                )}
              </List>
            </Grid>
          )}
          {!isEmptyObject(user.experience) && (
            <Grid
              item
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                padding: "10px 0",
              }}
            >
              <Typography component={"b"} variant={"b"}>
                {"Стаж:"}
              </Typography>
              <List
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))",
                  gridAutoFlow: "dense",
                }}
              >
                {user.experience?.scientific && (
                  <ListItem>
                    <ListItemText
                      primary={`Науковий:`}
                      secondary={user.experience?.scientific}
                    />
                  </ListItem>
                )}
                {user.experience?.pedagogical && (
                  <ListItem>
                    <ListItemText
                      primary={`Педагогічний:`}
                      secondary={user.experience?.pedagogical}
                    />
                  </ListItem>
                )}
                {user.experience?.scientificAndPedagogical && (
                  <ListItem>
                    <ListItemText
                      primary={`Науково-педагогічний:`}
                      secondary={user.experience?.scientificAndPedagogical}
                    />
                  </ListItem>
                )}
              </List>
            </Grid>
          )}
          {!isEmptyObject(user.position) && (
            <Grid
              item
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                padding: "10px 0",
              }}
            >
              <Typography component={"b"} variant={"b"}>
                {"Поточна посада:"}
              </Typography>
              <List
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))",
                  gridAutoFlow: "dense",
                }}
              >
                {user.position?.jobTitle && (
                  <ListItem>
                    <ListItemText
                      primary={`Посада (основна):`}
                      secondary={user.position?.jobTitle}
                    />
                  </ListItem>
                )}
                {user.position?.moonlighting && (
                  <ListItem>
                    <ListItemText
                      primary={`Посада (сумісництво):`}
                      secondary={user.position?.moonlighting}
                    />
                  </ListItem>
                )}
                {user.position?.nameOfDepartment && (
                  <ListItem>
                    <ListItemText
                      primary={`Назва кафедри:`}
                      secondary={user.position?.nameOfDepartment}
                    />
                  </ListItem>
                )}
                {user.position?.nameOfInstitute && (
                  <ListItem>
                    <ListItemText
                      primary={`Назва інституту (факультету):`}
                      secondary={user.position?.nameOfInstitute}
                    />
                  </ListItem>
                )}
                {user.position?.internshipStartDate && (
                  <ListItem>
                    <ListItemText
                      primary={`Дата початку стажу:`}
                      secondary={new Date(
                        user.position?.internshipStartDate
                      ).toLocaleDateString()}
                    />
                  </ListItem>
                )}
                {user.position?.startWorkingInInstitution && (
                  <ListItem>
                    <ListItemText
                      primary={`Початок роботи в закладі:`}
                      secondary={new Date(
                        user.position?.startWorkingInInstitution
                      ).toLocaleDateString()}
                    />
                  </ListItem>
                )}
              </List>
            </Grid>
          )}
        </Grid>
      </Grid>
    </ErrorBoundary>
  );
}

export default MainUserInfo;
